import React, { useState, useEffect } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTagHistories, getTagHistories } from '../../../slice/sensor.slice';

const exportToCSV = (rows, filename = 'rapport_capteurs.csv') => {
  if (!rows.length) return;
  const separator = ',';
  const keys = Object.keys(rows[0]);
  const csvContent = [
    keys.join(separator),
    ...rows.map(row => keys.map(k => JSON.stringify(row[k] ?? '')).join(separator))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

// Fonction asynchrone pour calculer les statistiques de l'historique
const calculateHistoriqueStats = async (selectedSensor, backendHistories = []) => {
  return new Promise((resolve) => {
    // Simuler un délai pour permettre au loader de s'afficher
    setTimeout(() => {
      const now = new Date();
      const since = new Date(now);
      since.setDate(now.getDate() - 7);

      // Utiliser les données du backend si disponibles, sinon fallback vers les données mock
      let historique = [];
      if (backendHistories && backendHistories.length > 0) {
        // Utiliser les données du backend
        historique = backendHistories
          .map(h => ({
            timestamp: new Date(h.timestamp || h.date || h.time),
            temperature: h.temperature || h.temp,
            humidity: h.humidity || h.hum,
            ouvert: h.isOpen || h.doorOpen || h.ouvert,
            battery: h.battery || h.batteryLevel
          }))
          .filter(h => h.timestamp >= since);
      } else if ('temperature' in selectedSensor && Array.isArray(selectedSensor.historique)) {
        // Fallback vers les données mock
        historique = selectedSensor.historique.filter((h) => new Date(h.timestamp) >= since);
      } else if ('ouvert' in selectedSensor && Array.isArray(selectedSensor.historique)) {
        // Fallback vers les données mock
        historique = selectedSensor.historique.filter((h) => new Date(h.timestamp) >= since);
      }

      // Statistiques pour capteur Temp/Hum
      if ('temperature' in selectedSensor && historique.length) {
        const tempVals = historique.map((h) => h.temperature);
        const humVals = historique.map((h) => h.humidity);
        const minTemp = Math.min(...tempVals);
        const maxTemp = Math.max(...tempVals);
        const avgTemp = (tempVals.reduce((a, b) => a + b, 0) / tempVals.length).toFixed(1);
        const minHum = Math.min(...humVals);
        const maxHum = Math.max(...humVals);
        const avgHum = (humVals.reduce((a, b) => a + b, 0) / humVals.length).toFixed(1);
        const tempMinExceeded = Array.prototype.filter.call(historique, (h) => h.temperature < selectedSensor.temperatureMin).length;
        const tempMaxExceeded = Array.prototype.filter.call(historique, (h) => h.temperature > selectedSensor.temperatureMax).length;
        const humMinExceeded = Array.prototype.filter.call(historique, (h) => h.humidity < selectedSensor.humidityMin).length;
        const humMaxExceeded = Array.prototype.filter.call(historique, (h) => h.humidity > selectedSensor.humidityMax).length;
        const horsConsigne = tempMinExceeded + tempMaxExceeded + humMinExceeded + humMaxExceeded;
        
        resolve({
          type: 'temperature',
          tempStats: {
            avgTemp,
            minTemp,
            maxTemp,
            avgHum,
            minHum,
            maxHum,
            tempMinExceeded,
            tempMaxExceeded,
            humMinExceeded,
            humMaxExceeded,
            horsConsigne
          }
        });
        return;
      }
      
      // Statistiques pour capteur Porte
      if ('ouvert' in selectedSensor && historique.length) {
        const ouvertures = Array.prototype.filter.call(historique, (h) => {
          if ('ouvert' in h) {
            return h.ouvert;
          }
          return false;
        }).length;
        
        let totalOuvert = 0, countOuvert = 0;
        for (let i = 1; i < historique.length; i++) {
          if (
            'ouvert' in historique[i-1] && historique[i-1] &&
            'ouvert' in historique[i] && !historique[i] &&
            'ouvert' in selectedSensor
          ) {
            const t0 = new Date(historique[i-1].timestamp).getTime();
            const t1 = new Date(historique[i].timestamp).getTime();
            totalOuvert += t1 - t0;
            countOuvert++;
          }
        }
        const avgOuvertMs = countOuvert ? totalOuvert / countOuvert : 0;
        const avgOuvertMin = avgOuvertMs / 60000;
        
        resolve({
          type: 'door',
          doorStats: {
            ouvertures,
            avgOuvertMin
          }
        });
        return;
      }
      
      resolve({ type: 'none' });
    }, 100); // Petit délai pour permettre au loader de s'afficher
  });
};

const RapportDisplay = ({ 
  selectedSensor, 
  onExportCSV 
}) => {
  const dispatch = useDispatch();
  const tagHistories = useSelector(getTagHistories);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [historiqueStats, setHistoriqueStats] = useState(null);

  // Fetch tag histories and calculate statistics
  useEffect(() => {
    if (!selectedSensor) {
      setHistoriqueStats(null);
      return;
    }

    const loadHistoriesAndStats = async () => {
      setIsLoadingStats(true);
      try {
        // Fetch histories from backend for the selected sensor
        console.log('Fetching histories for sensor:', selectedSensor.macAddr);
        const result = await dispatch(fetchTagHistories([selectedSensor.macAddr])).unwrap();
        console.log('RapportDisplay: fetchTagHistories result:', result);
        
        // Wait a moment for the Redux state to update
        setTimeout(async () => {
          try {
            // Get the updated histories from Redux state
            const sensorHistories = tagHistories[selectedSensor.macAddr] || [];
            console.log('Retrieved histories for sensor:', selectedSensor.macAddr, sensorHistories);
            
            // Calculate statistics with backend data
            const stats = await calculateHistoriqueStats(selectedSensor, sensorHistories);
            setHistoriqueStats(stats);
          } catch (error) {
            console.error('Erreur lors du calcul des statistiques avec données backend:', error);
            // Fallback to mock data calculation
            const stats = await calculateHistoriqueStats(selectedSensor, []);
            setHistoriqueStats(stats);
          }
        }, 100);
      } catch (error) {
        console.error('Erreur lors de la récupération des historiques:', error);
        // Fallback to mock data calculation
        try {
          const stats = await calculateHistoriqueStats(selectedSensor, []);
          setHistoriqueStats(stats);
        } catch (statError) {
          console.error('Erreur lors du calcul des statistiques de fallback:', statError);
          setHistoriqueStats({ type: 'none' });
        }
      } finally {
        setIsLoadingStats(false);
      }
    };

    loadHistoriesAndStats();
  }, [selectedSensor, dispatch, tagHistories]);

  if (!selectedSensor) {
    return (
      <div style={{ flex: 1, padding: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#666' }}>
          <h3>Aucun capteur sélectionné</h3>
          <p>Veuillez sélectionner un capteur dans la liste pour afficher son rapport.</p>
        </div>
      </div>
    );
  }

  // Normalise les données pour le tableau (un seul capteur)
  const rows = [
    'temperature' in selectedSensor
      ? {
          Type: 'Temp/Hum',
          Nom: selectedSensor.label,
          MAC: selectedSensor.macAddr,
          RSSI: selectedSensor.rssi,
          Température: selectedSensor.temperature,
          Humidité: selectedSensor.humidity,
          Batterie: selectedSensor.battery,
          DernierSignal: selectedSensor.lastSeen,
          Alertes: selectedSensor.alertes ? 'Oui' : 'Non',
          Rapports: selectedSensor.rapports ? 'Oui' : 'Non',
          Actif: selectedSensor.actif ? 'Oui' : 'Non',
        }
      : {
          Type: 'Porte',
          Nom: selectedSensor.label,
          MAC: selectedSensor.macAddr,
          RSSI: selectedSensor.rssi,
          Ouvert: selectedSensor.ouvert ? 'Oui' : 'Non',
          Batterie: selectedSensor.battery,
          DernierSignal: selectedSensor.lastSeen,
          Alertes: selectedSensor.alertes ? 'Oui' : 'Non',
          Rapports: selectedSensor.rapports ? 'Oui' : 'Non',
          Actif: selectedSensor.actif ? 'Oui' : 'Non',
        }
  ];

  const handleExportCSV = () => {
    exportToCSV(rows);
  };

  return (
    <div style={{ flex: 1, padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2>Rapport détaillé du capteur</h2>
        <Button 
          variant="contained" 
          color="primary" 
          onClick={handleExportCSV}
          disabled={!rows.length}
        >
          Exporter en CSV
        </Button>
      </div>

      {/* Rapport détaillé du capteur sélectionné */}
      <div style={{ border: '1px solid #ccc', borderRadius: 8, padding: 16, marginBottom: 24, background: '#f9f9fa' }}>
        <div><b>Type :</b> {'temperature' in selectedSensor ? 'Temp/Hum' : 'Porte'}</div>
        <div><b>Nom :</b> {selectedSensor.label}</div>
        <div><b>MAC :</b> {selectedSensor.macAddr}</div>
        <div><b>RSSI :</b> {selectedSensor.rssi}</div>
        {'temperature' in selectedSensor && <>
          <div><b>Température :</b> {selectedSensor.temperature}°C</div>
          <div><b>Humidité :</b> {selectedSensor.humidity}%</div>
        </>}
        {'ouvert' in selectedSensor && <div><b>Ouvert :</b> {selectedSensor.ouvert ? <span style={{ color: 'red', fontWeight: 600 }}>Oui</span> : 'Non'}</div>}
        <div><b>Batterie :</b> <span style={{ color: selectedSensor.battery < 30 ? 'red' : selectedSensor.battery < 60 ? 'orange' : 'green', fontWeight: 600 }}>{selectedSensor.battery}%</span></div>
        <div><b>Dernier signal :</b> {selectedSensor.lastSeen}</div>
        <div><b>Alertes :</b> {selectedSensor.alertes ? <span style={{ color: 'orange', fontWeight: 600 }}>Oui</span> : 'Non'}</div>
        <div><b>Rapports :</b> {selectedSensor.rapports ? 'Oui' : 'Non'}</div>
        <div><b>Actif :</b> {selectedSensor.actif ? <span style={{ color: 'green', fontWeight: 600 }}>Oui</span> : <span style={{ color: 'red', fontWeight: 600 }}>Non</span>}</div>
      </div>

      {/* Analyse de l'historique du capteur sélectionné */}
      <div style={{ border: '1px solid #dbeafe', borderRadius: 8, padding: 16, marginBottom: 24, background: '#f0f7ff' }}>
        <h3 style={{ marginTop: 0, color: '#2563eb' }}>Analyse de l'historique (7 derniers jours)</h3>
        {isLoadingStats ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
            <CircularProgress size={24} style={{ marginRight: '10px' }} />
            <span>Calcul des statistiques en cours...</span>
          </div>
        ) : (
          <>
            {historiqueStats?.type === 'temperature' && historiqueStats.tempStats && (
              <>
                <div><b>Température moyenne :</b> {historiqueStats.tempStats.avgTemp}°C <b>Min :</b> {historiqueStats.tempStats.minTemp}°C <b>Max :</b> {historiqueStats.tempStats.maxTemp}°C</div>
                <div><b>Humidité moyenne :</b> {historiqueStats.tempStats.avgHum}% <b>Min :</b> {historiqueStats.tempStats.minHum}% <b>Max :</b> {historiqueStats.tempStats.maxHum}%</div>
                <div><b>Nb de dépassements de seuils :</b> Température basse : <span style={{color:'#2563eb'}}>{historiqueStats.tempStats.tempMinExceeded}</span>, haute : <span style={{color:'#ef4444'}}>{historiqueStats.tempStats.tempMaxExceeded}</span> | Humidité basse : <span style={{color:'#0ea5e9'}}>{historiqueStats.tempStats.humMinExceeded}</span>, haute : <span style={{color:'#a21caf'}}>{historiqueStats.tempStats.humMaxExceeded}</span></div>
                <div><b>Durée cumulée hors consigne :</b> {historiqueStats.tempStats.horsConsigne} mesures</div>
              </>
            )}
            {historiqueStats?.type === 'door' && historiqueStats.doorStats && (
              <>
                <div><b>Nb d'ouvertures sur 7j :</b> {historiqueStats.doorStats.ouvertures}</div>
                <div><b>Durée moyenne d'ouverture :</b> {historiqueStats.doorStats.avgOuvertMin ? historiqueStats.doorStats.avgOuvertMin.toFixed(1) + ' min' : '-'}</div>
              </>
            )}
            {historiqueStats?.type === 'none' && (
              <div>Aucune donnée historique sur les 7 derniers jours.</div>
            )}
          </>
        )}

        {/* Alertes récentes */}
        <div style={{ marginTop: 18 }}>
          <b>Alertes récentes (7 jours)&nbsp;:</b>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(selectedSensor.alertesList || []).filter(a => new Date(a.timestamp) >= new Date(Date.now() - 7*24*60*60*1000)).length === 0 && <li>Aucune alerte</li>}
            {(selectedSensor.alertesList || []).filter(a => new Date(a.timestamp) >= new Date(Date.now() - 7*24*60*60*1000)).map(a => (
              <li key={a.id} style={{ color: a.severity === 'critical' ? '#dc2626' : (a.severity === 'warning' ? '#f59e42' : '#2563eb') }}>
                [{new Date(a.timestamp).toLocaleString()}] {a.message}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Tableau des données */}
      <table style={{ width: '100%', marginTop: 16, borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {Object.keys(rows[0] || {}).map((col, idx) => (
              <th key={idx} style={{ border: '1px solid #ccc', padding: 8, background: '#f5f5f5' }}>{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx}>
              {Object.values(row).map((val, colIdx) => (
                <td key={colIdx} style={{ border: '1px solid #eee', padding: 8 }}>{val}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RapportDisplay;
